import { Dispatch, SetStateAction, useState } from 'react';
import {
  Control,
  Controller,
  SubmitHandler,
  UseFormHandleSubmit,
  UseFormReset,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { SharedDrivesSearchForm } from '../../../../../drive/shared-drives/shared-drives-admin-grid-with-tree';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import ClearAll from '@mui/icons-material/ClearAll';
import Search from '@mui/icons-material/Search';

interface SharedDrivesGridFiltersProps {
  control: Control<SharedDrivesSearchForm>;
  handleSubmit: UseFormHandleSubmit<SharedDrivesSearchForm>;
  reset: UseFormReset<SharedDrivesSearchForm>;
  isLoading: boolean;
  isDirty: boolean;
  paginationModel: {
    pageSize: number;
    page: number;
    cursor: string | null;
    search: string;
    driveId: string;
  };
  setPaginationModel: Dispatch<
    SetStateAction<{
      pageSize: number;
      page: number;
      cursor: string | null;
      search: string;
      driveId: string;
    }>
  >;
  watch: UseFormWatch<SharedDrivesSearchForm>;
  setValue: UseFormSetValue<SharedDrivesSearchForm>;
}

export default function SharedDrivesGridFilters({
  control,
  handleSubmit,
  reset,
  isLoading,
  isDirty,
  paginationModel,
  setPaginationModel,
  watch,
  setValue,
}: SharedDrivesGridFiltersProps) {
  const [error, setError] = useState(false);

  const onSubmit: SubmitHandler<SharedDrivesSearchForm> = (data) => {
    if (data.name.length > 0) {
      setPaginationModel({
        pageSize: 100,
        page: 0,
        cursor: null,
        search: data.name,
        driveId: '',
      });
    } else if (data.driveId.length > 0) {
      setPaginationModel({
        pageSize: 100,
        page: 0,
        cursor: null,
        search: '',
        driveId: data.driveId,
      });
    }
  };

  const name = watch('name') || '';
  const driveId = watch('driveId') || '';

  return (
    <Card sx={{ p: 1.5, height: '100%', overflowY: 'auto' }}>
      <Stack gap={2}>
        <Tooltip
          arrow
          title={
            driveId.length > 0
              ? 'Only one filter at a time is allowed.'
              : 'This filter searches for shared drives whose names contain the whole word provided.'
          }
        >
          <span>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'name',
                  }}
                  InputLabelProps={{
                    htmlFor: 'name',
                  }}
                  label="Name"
                  value={field.value}
                  disabled={driveId.length > 0}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                    if (error) {
                      setError(false);
                    }
                    if (driveId.length > 0) {
                      setValue('driveId', '');
                    }
                  }}
                  sx={{ width: 300 }}
                  autoComplete="off"
                />
              )}
            />
          </span>
        </Tooltip>
        <Tooltip
          arrow
          title={name.length > 0 ? 'Only one filter at a time is allowed.' : ''}
        >
          <span>
            <Controller
              name="driveId"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'driveId',
                  }}
                  InputLabelProps={{
                    htmlFor: 'driveId',
                  }}
                  label="Drive ID"
                  value={field.value}
                  disabled={name.length > 0}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                    if (error) {
                      setError(false);
                    }
                    if (name.length > 0) {
                      setValue('name', '');
                    }
                  }}
                  fullWidth
                  autoComplete="off"
                />
              )}
            />
          </span>
        </Tooltip>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          startIcon={<Search />}
          disabled={isLoading || !isDirty}
        >
          Search
        </Button>
        <Tooltip
          arrow
          title={
            paginationModel.search.length !== 0 ||
            paginationModel.driveId.length !== 0
              ? 'This will clear your fields above and reset the grid back to the original starting state.'
              : ''
          }
        >
          <span>
            <Button
              variant="outlined"
              onClick={() => {
                if (error) {
                  setError(false);
                }
                reset();
                setPaginationModel({
                  pageSize: 50,
                  page: 0,
                  cursor: null,
                  search: '',
                  driveId: '',
                });
              }}
              sx={{ width: 300 }}
              startIcon={<ClearAll />}
              disabled={
                isLoading ||
                (paginationModel.search.length === 0 &&
                  paginationModel.driveId.length === 0)
              }
            >
              Clear Filters
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Card>
  );
}
